import React, {useCallback, useState} from 'react';
import {ScreenProps} from '../../../../interfaces/screens/mainScreen';
import MainLayout from '../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import TextGeneric from '../../../../components/textGeneric';
import Icon from '../../../../components/icon';
import {Theme} from '../../../../theme/lightTheme';
import screens from '../../../../config/screens';
import {useFocusEffect, useRoute} from '@react-navigation/native';
import {deleteNote, getNoteById} from '../actions';
import GenericButton from '../../../../components/button';
import {tr} from '../../../../translations';
import dayjs from 'dayjs';
import {Space} from '../../home';
import {modalTypes} from '../../../../utils/enums';
import {openQuestionModal} from '../../../../connected-components/questionModal/actions';
import {View} from 'react-native';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInLeft,
} from 'react-native-reanimated';

const styles = {
  container: {
    paddingHorizontal: '5%' as any,
    alignSelf: 'center' as const,
    alignItems: 'center' as const,
    paddingTop: '5%' as any,
    width: '100%' as any,
  },
  headerRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    marginBottom: 8,
    width: '100%' as any,
  },
  dateRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    marginTop: 15,
    marginBottom: 8,
    width: '100%' as any,
  },
  descContainer: {
    flex: 1,
    width: '100%' as any,
  },
  iconButton: (theme: Theme, isError?: boolean) => ({
    marginHorizontal: theme.text.s55 / 2,
    ...(isError && {borderColor: theme.text.error}),
  }),
  articleButton: (theme: Theme) => ({
    alignSelf: 'flex-end' as const,
    minWidth: theme.text.s7 * 10,
  }),
};

const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.primary};
  text-align: left;
  width: 100%;
`;

const Description = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.title};
  text-align: left;
  padding-top: 8%;
  flex: 1;
`;

const Date = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme}) => theme.text.description};
  text-align: left;
`;

const TitleContainer = styled.View`
  flex: 1;
`;

const IconContainer = styled.TouchableOpacity<{
  isSmall?: boolean;
  isPrimary?: boolean;
}>`
  width: ${({theme, isSmall}) => theme.text.s1 * (isSmall ? 1 : 1.1)}px;
  height: ${({theme, isSmall}) => theme.text.s1 * (isSmall ? 1 : 1.1)}px;
  background-color: ${({theme, isPrimary}) =>
    isPrimary ? theme.primary : theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
`;

/**
 * ViewNote screen component.
 *
 * @param {ScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const ViewNote: React.FC<ScreenProps> = ({navigation}) => {
  const {bottom: bottomInset} = useSafeAreaInsets();
  const theme = useTheme() as Theme;
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();

  const {id, title, description, date, news} = useRoute<any>().params || {};
  const [form, setForm] = useState({
    id,
    title,
    description,
    date,
    news,
  });

  useFocusEffect(
    useCallback(() => {
      (async () => {
        const res = await getNoteById(id);
        setForm(res);
      })();
    }, [id]),
  );

  // Handlers
  const handleDeletePress = () => {
    openQuestionModal({
      customHeaderComponent: () => (
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingBottom: theme.text.s1,
          }}>
          <Icon
            name="delete"
            size={theme.text.s1 * 2}
            color={theme.text.error}
            style={{paddingBottom: '5%'}}
          />
        </View>
      ),
      title: tr('notes.deleteTitle'),
      message: tr('notes.deleteMessage'),
      type: modalTypes.question,
      onConfirm: async () => {
        await deleteNote(id);
        navigation?.goBack();
      },
    });
  };

  const handleEditPress = () => {
    navigation?.navigate(screens.content.addNote, {
      isEdit: true,
      id,
      title: form?.title,
      description: form?.description,
    });
  };

  const handleOpenArticle = () => {
    const {news} = form;
    navigation?.navigate(screens.content.latestArticle, {
      item: news,
      isSummary: news?.isSummary,
      isRecommendation: news?.isRecommendation,
      isRecommendationByTab: news?.isRecommendationByTab,
    });
  };

  return (
    <MainLayout
      showOverlay={false}
      enableScroll
      backHeader
      noPadding
      noPaddingHeader>
      <Animated.View
        entering={FadeInDown.duration(400).springify()}
        style={[styles.container, {width: getByScreenSize('100%', '70%')}]}>
        {/* Header section */}
        <Animated.View
          entering={FadeIn.delay(150).duration(300)}
          style={styles.headerRow}>
          <TitleContainer>
            <Animated.View entering={FadeInLeft.delay(250).duration(350)}>
              <Title bold>{form?.title}</Title>
            </Animated.View>
          </TitleContainer>

          <Animated.View entering={FadeIn.delay(350).duration(300)}>
            <IconContainer
              onPress={handleDeletePress}
              style={[styles.iconButton(theme, true)]}>
              <Icon
                name="delete"
                size={theme.text.s55}
                color={theme.text.error}
              />
            </IconContainer>
          </Animated.View>

          <Animated.View entering={FadeIn.delay(400)}>
            <IconContainer onPress={handleEditPress}>
              <Icon name="edit" color={theme.primary} size={theme.text.s55} />
            </IconContainer>
          </Animated.View>
        </Animated.View>

        <Animated.View entering={FadeIn.delay(450)} style={styles.dateRow}>
          <Animated.View entering={FadeInDown.delay(500)}>
            <Date semiBold>{dayjs(form?.date).format('DD MMM YYYY')}</Date>
          </Animated.View>

          <Animated.View entering={FadeInDown.delay(550)}>
            <GenericButton
              label={tr('notes.openArticle')}
              onPress={handleOpenArticle}
              rightIconName="article"
              rightIconType="MaterialIcons"
              isSmall
              style={styles.articleButton(theme)}
            />
          </Animated.View>
        </Animated.View>

        <Animated.View
          entering={FadeInDown.delay(600)}
          style={styles.descContainer}>
          <Description>{form?.description}</Description>
        </Animated.View>

        <Space bottomInset={bottomInset} extraSpace={hdp(12)} />
      </Animated.View>
    </MainLayout>
  );
};

export default ViewNote;
